// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';
import { ContextBanner } from '../../../App.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// types
import {
	Account,
	ErrorCause,
	ExchangeRateByDate,
	Feedback,
	Loan,
	PaymentPayload,
	TransactionMetadata,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Grid, SelectChangeEvent } from '@mui/material';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// fields
import { Amounts } from '../../fields/Amounts/Amounts';
import { CheckboxConfirmation } from '../../fields/CheckboxConfirmation/CheckboxConfirmation';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input } from '../../fields/Input/Input';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import { translateError } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormAddAccountBalanceValues {
	amounts: Record<string, number>;
	date: string;
	comments: string;
}
export interface FormAddAccountBalanceProps {
	account: Account;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddAccountBalance = ({
	account,
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddAccountBalanceProps) => {
	// hooks
	const { t } = useTranslation();
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	const currenciesForExchangeRates = getCurrenciesForExchangeRates({ currency: account.currency });
	const exchangeRatesDefault: ExchangeRateByDate[] = currenciesForExchangeRates.map((currency) => ({
		currency: currency,
		dateExchangeRate: '',
		exchangeRate: 0,
	}));

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>(exchangeRatesDefault);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [values, setValues] = useState<FormAddAccountBalanceValues>({
		amounts: {},
		date: formatDate({ date: new Date(), format: 'YYYY-MM-DD' }),
		comments: '',
	});

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const formId = 'form-add-account-balance';

	const balance = account.balances.reduce((total, account) => {
		return total + account.balance;
	}, 0);

	// check if from account has disabled loans
	let hasDisabledLoans = false;

	for (let i = 0; i < loans.length; i++) {
		if (loans[i]?.status === 'DISABLED') {
			hasDisabledLoans = true;
			break;
		}
	}

	// fetch loans
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=financier,id,code,status&populate=loans`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		// define balances using loans
		const payments: PaymentPayload[] = [];
		let total = 0;

		for (let s = 0; s < loans.length; s++) {
			const amount = Number(entries[`source-${s}-amount`]);
			if (amount > 0) {
				total += amount;

				// Define conversions using exchange rates
				const conversions = exchangeRates.map((exchangeRate, indexExchangeRate) => {
					const conversionAmount =
						Number(entries[`source-${s}-amount-conversion-${indexExchangeRate}`]) || 0;
					const conversionTax =
						Number(entries[`source-${s}-tax-conversion-${indexExchangeRate}`]) || 0;

					return {
						amount: conversionAmount,
						currency: exchangeRate.currency,
						dateExchangeRate: exchangeRate.dateExchangeRate,
						exchangeRate: exchangeRate.exchangeRate,
						tax: conversionTax,
					};
				});

				payments.push({
					amount,
					conversions: conversions.length > 0 ? conversions : [],
					currency: account.currency,
					loan: loans[s].id,
				});
			}
		}

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (total === 0) {
				throw new Error('The amount needs to be greater than 0.', {
					cause: {
						id: 'TRANSACTION_AMOUNT_NON_EXISTENT',
					},
				});
			}

			// add metadata
			const metadata: TransactionMetadata = {
				uuid: crypto.randomUUID(),
			};

			const body = {
				date: formatDate({
					date: new Date(values.date as string),
					format: 'YYYY-MM-DD',
				}),
				metadata,
				payments,
				project: project?.id,
				status: 'COMPLETED',
				toAccount: account.id,
				type: 'REPLENISHMENT',
			};

			const fetchPostTransaction = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
				method: 'post',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resPostTransaction = await fetchPostTransaction.json();

			if (resPostTransaction.error) {
				throw new Error(resPostTransaction.error.message, {
					cause: {
						details: resPostTransaction.error.details,
						id: resPostTransaction.error.id,
					},
				});
			}

			const transaction = resPostTransaction.data;

			// post comments
			if (values.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: values.comments,
						transaction: transaction.id,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					throw new Error(resPostComment.error.message, {
						cause: {
							details: resPostComment.error.details,
							id: resPostComment.error.id,
						},
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.accountBalanceUpdated', { name: account.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause?.id === 'TRANSACTION_EXCEEDS_LOAN_AMOUNT') {
				cause.details.loanAmount = formatCurrency({
					currency: cause.details?.currency,
					value: cause.details?.loan?.amount,
				});

				cause.details.disbursedAmount = formatCurrency({
					currency: cause.details?.currency,
					value: cause.details?.totalDisbursed,
				});
			}

			// set feedback
			setFeedback({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	return (
		<form id={formId} name="formAddAccountBalance" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						defaultValue={formatDate({ date: new Date(), format: 'YYYY-MM-DD' })}
						id={`${formId}-date`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('date')}
						name="date"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.date}
					/>
				</Grid>
				{featureFlags['IFRS'] && exchangeRates.length > 0 && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={account.currency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Amounts
						currency={account.currency}
						exchangeRates={exchangeRates}
						formId={formId}
						hasCurrency={false}
						loans={loans}
						onChange={(e, total) => {
							const amountsNew: FormAddAccountBalanceValues['amounts'] = { ...values.amounts };
							const indexSource = e.target.getAttribute('data-index');

							amountsNew[indexSource as string] = e.target.value as unknown as number;

							setValues((prevState) => {
								return {
									...prevState,
									amounts: amountsNew,
									total,
								};
							});
						}}
						summaries={[
							{
								operator: '+',
								title: t('newBalance'),
								value: balance,
							},
						]}
						title={t('fundsToAdd')}
					/>
				</Grid>
				{hasDisabledLoans && (
					<Grid item xs={12}>
						<Alert severity="info">{t('alert.financingSourcesDisabled')}</Alert>
					</Grid>
				)}
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
							onChange={handleChangeValue}
							value={values.comments}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<CheckboxConfirmation formId={formId} />
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
